if (!customElements.get('py-look-collection-v8')) {
    customElements.define('py-look-collection-v8', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;

        if (!this.querySelector('[data-pylc-ready]')) {
          this.observer?.disconnect();
          this.observer = new MutationObserver(() => this.connectedCallback());
          this.observer.observe(this, { childList: true, subtree: true });
          return;
        }

        this.observer?.disconnect();
        this.button = this.querySelector('[data-pylc-add]');

        if (!this.button) return;

        this.bound = true;
        this.listeners = new AbortController();
        this.label = this.querySelector('[data-pylc-add-label]');
        this.defaultLabel = this.defaultLabel || this.label.textContent;
        this.message = this.querySelector('[data-pylc-message]');
        this.currency = this.dataset.currency;

        try {
          this.formatter = new Intl.NumberFormat(
            this.dataset.locale || document.documentElement.lang || 'en',
            {
              style: 'currency',
              currency: this.currency,
              currencyDisplay: 'code'
            }
          );
        } catch {
          this.formatter = null;
        }

        this.addEventListener('change', event => {
          if (this.busy) return;

          if (event.target.matches('[data-pylc-select]') && event.target.closest('[data-pylc-row]').dataset.kind === 'look') {
            event.target
              .closest('[data-pylc-row]')
              .querySelector('[data-pylc-check]')
              .checked = true;
          }

          if (!this.uncertain) this.message.textContent = '';
          this.update();
        }, { signal: this.listeners.signal });

        this.addEventListener('click', event => {
          if (event.target.closest('[data-pylc-add]')) this.addToCart();

          const all = event.target.closest('[data-pylc-select-all]');
          const clear = event.target.closest('[data-pylc-clear]');

          if ((all || clear) && !this.busy) {
            for (const check of this.querySelectorAll('[data-pylc-check]')) {
              check.checked = Boolean(all) && !check.disabled && check.closest('[data-pylc-row]').dataset.kind === 'look';
            }
            this.update();
          }
        }, { signal: this.listeners.signal });

        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.listeners?.abort();
        this.bound = false;

        if (this.busy) {
          this.uncertain = true;
          this.requestController?.abort();
        }
      }

      money(amount) {
        return this.formatter
          ? this.formatter.format(amount / 100)
          : `${this.currency || ''} ${(amount / 100).toFixed(2)}`.trim();
      }

      update() {
        const merged = new Map();
        let selectedTotal = 0;
        let fullTotal = 0;
        let fullCompare = 0;
        let baseCount = 0;
        let selectedCount = 0;
        const eligibleUnits = [];
        let complete = true;
        let unavailable = 0;
        const rows = this.querySelectorAll('[data-pylc-row]');

        for (const row of rows) {
          const select = row.querySelector('[data-pylc-select]');
          const option = select?.selectedOptions[0];
          const check = row.querySelector('[data-pylc-check]');
          const quantity = Number(row.dataset.quantity);
          const price = Number(option?.dataset.price);

          const priced =
            option?.dataset.price !== undefined &&
            option.dataset.price !== '' &&
            Number.isFinite(price) &&
            price >= 0 &&
            Number.isInteger(quantity) &&
            quantity > 0;

          const validId = /^\d+$/.test(option?.value || '');
          const available = priced && validId && option.dataset.available === 'true';

          const base = row.dataset.kind === 'look';
          const compare = Math.max(price || 0, Number(option?.dataset.compare) || 0);
          if (base) {
            baseCount++;
            if (priced && validId) {
              fullTotal += price * quantity;
              fullCompare += compare * quantity;
            } else {
              complete = false;
            }
          }

          if (!available) {
            if (base) unavailable++;
            check.checked = false;
          }

          check.disabled = !available || Boolean(this.busy);
          select.disabled = Boolean(this.busy) || select.options.length < 2;

          const status = row.querySelector('[data-pylc-row-status]');
          status.hidden = available;
          status.textContent = priced ? this.dataset.unavailable : this.dataset.pending;

          row.querySelector('[data-pylc-row-price]').textContent = priced
            ? (option.dataset.money || this.money(price * quantity))
            : this.dataset.pending;

          const compareNode = row.querySelector('[data-pylc-row-compare]');
          if (compareNode) {
            compareNode.hidden = !priced || compare <= price;
            compareNode.textContent = priced ? this.money(compare * quantity) : '';
          }

          if (option?.dataset.url) {
            for (const link of row.querySelectorAll('[data-pylc-product-link]')) {
              link.href = option.dataset.url;
            }
          }

          const thumb = this.querySelector(
            `[data-pylc-thumb-index="${row.dataset.index}"]`
          );

          if (thumb && option?.dataset.url) {
            const link = thumb.querySelector('[data-pylc-thumb-link]');
            if (link) link.href = option.dataset.url;
          }

          if (option?.dataset.image) {
            for (const photo of [
              row.querySelector('.py-look-collection-v8__product-photo'),
              thumb?.querySelector('img')
            ]) {
              if (photo && photo.getAttribute('src') !== option.dataset.image) {
                photo.removeAttribute('srcset');
                photo.src = option.dataset.image;
              }
            }
          }

          if (!check.checked || !available) continue;

          selectedTotal += price * quantity;
          selectedCount += quantity;
          if (this.dataset.promo === 'true' && row.dataset.eligible === 'true') {
            eligibleUnits.push({ price, quantity });
          }
          const item = merged.get(option.value) || {
            id: option.value,
            quantity: 0
          };

          item.quantity += quantity;
          merged.set(option.value, item);
        }

        this.items = [...merged.values()];

        this.querySelector('[data-pylc-full-total]').textContent =
          baseCount && complete ? this.money(fullTotal) : this.dataset.pending;
        const fullCompareNode = this.querySelector('[data-pylc-full-compare]');
        fullCompareNode.hidden = !baseCount || !complete || fullCompare <= fullTotal;
        fullCompareNode.textContent = this.money(fullCompare);

        this.querySelector('[data-pylc-selected-total]').textContent =
          this.money(selectedTotal);

        this.querySelector('[data-pylc-selection-count]').textContent = (this.dataset.itemsLabel || '').replace('{count}', selectedCount);
        const promoSummary = this.querySelector('[data-pylc-promo-summary]');
        let savings = 0;
        if (promoSummary) {
          promoSummary.hidden = false;
          eligibleUnits.sort((a, b) => a.price - b.price);
          const eligibleCount = eligibleUnits.reduce((sum, unit) => sum + unit.quantity, 0);
          const freeCount = Math.floor(eligibleCount / 3);
          let remaining = freeCount;
          for (const unit of eligibleUnits) {
            const freeUnits = Math.min(unit.quantity, remaining);
            savings += freeUnits * unit.price;
            remaining -= freeUnits;
            if (!remaining) break;
          }
          const threshold = Math.max(3, Math.ceil(eligibleCount / 3) * 3);
          const missing = 3 - eligibleCount % 3;
          const progress = this.querySelector('[data-pylc-progress]');
          progress.max = threshold;
          progress.value = eligibleCount;
          this.querySelector('[data-pylc-eligible-count]').textContent = `${eligibleCount} / ${threshold} ${this.dataset.eligibleLabel}`;
          this.querySelector('[data-pylc-promo-hint]').textContent = freeCount
            ? (freeCount === 1 ? this.dataset.unlockedOne : this.dataset.unlocked).replace('{count}', freeCount).replace('{savings}', this.money(savings))
            : (missing === 1 ? this.dataset.addOne : this.dataset.addMore).replace('{count}', missing);
          this.querySelector('[data-pylc-savings-row]').hidden = freeCount === 0;
          this.querySelector('[data-pylc-estimated-row]').hidden = freeCount === 0;
          this.querySelector('[data-pylc-promo-savings]').textContent = `−${this.money(savings)}`;
          this.querySelector('[data-pylc-estimated-total]').textContent = this.money(selectedTotal - savings);
          this.button.dataset.promoUnlocked = String(freeCount > 0);
        }

        this.button.disabled =
          Boolean(this.busy) ||
          Boolean(this.uncertain) ||
          !this.items.length;

        this.label.textContent = this.busy
          ? this.dataset.loading
          : savings > 0
            ? this.dataset.promoCta.replace('{savings}', this.money(savings)).replace('{total}', this.money(selectedTotal - savings))
            : this.defaultLabel;

        this.querySelector('[data-pylc-partial]').hidden = unavailable === 0;

        for (const control of this.querySelectorAll(
          '[data-pylc-select-all], [data-pylc-clear]'
        )) {
          control.disabled = Boolean(this.busy);
        }
      }

      async addToCart() {
        if (this.busy || this.uncertain) return;

        this.update();

        if (!this.items.length) {
          this.message.textContent = this.dataset.empty;
          return;
        }

        const items = this.items.map(item => ({ ...item }));
        this.busy = true;
        this.message.textContent = '';
        this.requestController = new AbortController();
        this.update();

        let sent = false;

        try {
          const root = window.Shopify?.routes?.root || '/';
          sent = true;

          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ items }),
            signal: this.requestController.signal
          });

          const result = await response.json();

          if (!response.ok) {
            this.message.textContent =
              typeof result.description === 'string'
                ? result.description
                : typeof result.message === 'string'
                  ? result.message
                  : this.dataset.error;

            if (response.status >= 500) {
              this.uncertain = true;
              this.message.textContent += ` ${this.dataset.uncertain}`;
              this.querySelector('[data-pylc-cart]').hidden = false;
            }

            return;
          }

          this.message.textContent = this.dataset.success;
          this.querySelector('[data-pylc-cart]').hidden = false;

          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
        } catch {
          this.uncertain = sent;
          this.message.textContent = sent
            ? this.dataset.uncertain
            : this.dataset.error;

          this.querySelector('[data-pylc-cart]').hidden = !sent;
        } finally {
          this.busy = false;
          this.requestController = null;
          this.update();
        }
      }
    });
  }
